import React from 'react';
import { View, StyleProp, ViewStyle } from 'react-native';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { useThemeColors } from '@/hooks/shared/useThemeColors';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import {
  IconStatCardList,
  type IconStatCardItem,
  type IconStatCardLayout,
} from './IconStatCard';
import { SectionHeader } from '../SectionHeader';

interface CommunityResourcesSectionProps {
  onMarketplace: () => void;
  onResources: () => void;
  onEvents: () => void;
  onWifi: () => void;
  /**
   * Opens Linked Accounts. Optional so existing callers keep compiling, but the
   * home screen is expected to pass it: this tile is what replaced the standalone
   * `LinkedAccountsCard` there, and the tile is omitted entirely without it.
   */
  onLinkedAccountsPress?: () => void;
  /**
   * `row` (default) is the mobile edge-bleeding horizontal scroller; `grid`
   * wraps the same cards two-up inside a desktop column.
   */
  layout?: IconStatCardLayout;
  /** Page padding the `row` layout bleeds past. Defaults to the 24pt page gutter. */
  horizontalPadding?: number;
  /** Extra per-card sizing, applied after the layout's own. */
  itemStyle?: StyleProp<ViewStyle>;
  /**
   * Tightens the section header's vertical padding — for the no-scroll home,
   * where header whitespace is height the planner does not get. Off by default.
   */
  denseHeader?: boolean;
  style?: StyleProp<ViewStyle>;
}

export const CommunityResourcesSection: React.FC<CommunityResourcesSectionProps> = React.memo(({
  onMarketplace,
  onResources,
  onEvents,
  onWifi,
  onLinkedAccountsPress,
  layout,
  horizontalPadding,
  itemStyle,
  denseHeader = false,
  style,
}) => {
  const { colors } = useThemeColors();

  const items: IconStatCardItem[] = [
    {
      key: 'marketplace',
      label: 'Marketplace',
      caption: 'Buy & sell',
      iconBg: colors.accent.purpleLight,
      Icon: RESIDENTS_SVG_COMPONENTS.SUPERMARKET,
      iconColor: PRIMITIVE_COLORS.resioPurple,
      onPress: onMarketplace,
    },
    {
      key: 'resources',
      label: 'Resources',
      caption: 'Guides & docs',
      iconBg: colors.accent.orangeLight,
      Icon: RESIDENTS_SVG_COMPONENTS.DOCUMENT,
      iconColor: PRIMITIVE_COLORS.warningOrange,
      onPress: onResources,
    },
    {
      key: 'events',
      label: 'Events',
      caption: 'Community events',
      iconBg: colors.accent.greenLight,
      Icon: RESIDENTS_SVG_COMPONENTS.CALENDAR,
      iconColor: PRIMITIVE_COLORS.successGreen,
      onPress: onEvents,
    },
    {
      key: 'wifi',
      label: 'WiFi',
      caption: 'Connection info',
      iconBg: colors.accent.blueBgLight,
      Icon: RESIDENTS_SVG_COMPONENTS.WIFI,
      iconColor: PRIMITIVE_COLORS.resioBlue,
      onPress: onWifi,
    },
  ];

  if (onLinkedAccountsPress) {
    items.push({
      key: 'linkedAccounts',
      label: 'Linked Accounts',
      caption: 'Family access',
      // A deeper tint of the same blue as WiFi — the only remaining accent
      // fill; the icon is the one the Linked Accounts destination already uses
      // in the nav, so the tile and the screen read as the same thing.
      iconBg: colors.accent.blueBgMedium,
      Icon: RESIDENTS_SVG_COMPONENTS.LINKED_ACCOUNTS,
      iconColor: PRIMITIVE_COLORS.resioBlue,
      onPress: onLinkedAccountsPress,
    });
  }

  return (
    <View style={style}>
      <SectionHeader title="My Community" dense={denseHeader} />
      <IconStatCardList
        items={items}
        layout={layout}
        horizontalPadding={horizontalPadding}
        itemStyle={itemStyle}
      />
    </View>
  );
});

CommunityResourcesSection.displayName = 'CommunityResourcesSection';
